import { faEye, faEyeSlash } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import { makeStyles } from '@material-ui/core/styles';
import TextField from "@material-ui/core/TextField";
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import i18next from 'i18next';
import React, { useEffect, useRef } from 'react';
import Button from 'react-bootstrap-button-loader';
import { withNamespaces } from 'react-i18next';
import { format } from 'react-string-format';
import { updatePassword } from '../../actions/password';
import * as statusComplexity from '../../scripts/ComplexityPasswordStatusCode';
import * as StatusCode from '../../scripts/GenericStatusCode';
import * as PasswordStatusCode from '../../scripts/PasswordStatusCode';
import stylesL from '../../styles/login.module.css';
import { encryptPassword, getConnectionStringNamePrefix } from '../../utils';
import { encryption } from '../../utils/encryption';
import { validatePasswordComplexity } from '../../utils/passwordComplexity';
import * as storage from '../../utils/storage';
import { UserCode } from '../../utils/users';
import AlertAlgo from '../controls/AlertSnack';
import PasswordTooltip from './PasswordTooltip';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        paddingLeft: '10px',
        paddingRight: '10px',
        paddingTop: '10px'
    },
    h1: {
        fontWeight: 'bold'
    },
    button: {
        marginTop: '20px',
        paddingBottom: '20px',
        float: 'right',
    },
    formControl: {
        marginTop: '10px'
    },
    descriptionPassword: {
        textAlign: 'justify'
    },
    lightBulbIcon: {
        position: 'absolute',
        left: 0,
        paddingLeft: 0,
        paddingTop: 0,
        cursor: 'pointer',
        fontSize: '24px',
        color: 'var(--color1)',
        '&:hover': {
            opacity: 0.7
        }
    },
    saveButtonLine: {
        marginTop: '15px',
        position: 'relative'
    },
    nextButton: {
        float: 'right'
    }
}));

function ChangePassword(props) {
    const classes = useStyles();
    const [oldPassword, setOldPassword] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [descriptionPassword, setDescriptionPassword] = React.useState('');
    const [genericError, setGenericError] = React.useState(`${i18next.t('password:validation.message.generic-error')}`);
    const [isSaved, setIsSaved] = React.useState(false);
    const [applyComplexity, setApplyComplexity] = React.useState(false);
    const [minLength, setMinLength] = React.useState(0);
    const [showOldPassword, setShowOldPassword] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = React.useState(false);

    const alertRef = useRef(null);

    useEffect(() => {
        resize();
        setApplyComplexity(storage.getIsPasswordComplex());
        setMinLength(storage.getMinLengthPassword());
        handleMessagePassword();
    }, []);

    const resize = () => {
        setTimeout(function () {
            const height = window.innerHeight;
            const titleBar = document.querySelector('#appBarTitle').clientHeight;

            const customHeight = height - titleBar - 50;

            const element = document.querySelector('#divPassword');

            if (element !== undefined && element !== null) {
                element.style.height = `${customHeight}px`;
                element.style.overflowX = 'hidden';
            }
        }, 1000);
    }

    const validatePassword = () => {
        return new Promise(async (resolve) => {
            let message = '';
            let minPasswordLength = minLength > 0 ? minLength : 3;

            if (password.length === 0 || confirmPassword.length === 0 || oldPassword.length === 0) {
                message = i18next.t('password:validation.message.empty-fields');
            } else if (password !== confirmPassword) {
                message = i18next.t('password:validation.message.confirm-password');
            } else if (password.length < minPasswordLength) {
                message = format(i18next.t('password:validation.message.password-length'), minPasswordLength);
            } else if (applyComplexity) {
                // Complexity
                await validatePasswordComplexity(password).then(status => {
                    if (status === statusComplexity.Error_Number_SpecialCharacter) {
                        message = i18next.t('password:validation.message.number-specialchar');
                    } else if (status === statusComplexity.Error_Two_Letters) {
                        message = i18next.t('password:validation.message.min-two-letters');
                    } else if (status === statusComplexity.Error_Min_Maj_Letters) {
                        message = i18next.t('password:validation.message.min-maj-letters');
                    } else if (status !== statusComplexity.Success) {
                        message = i18next.t('error:generic-error');
                    }
                });
            }

            return resolve(message);
        });
    }

    // This is only if is a popup
    const handleClose = (success) => {
        props.handleClose(success);
    }

    const handleSave = async () => {
        setIsSaved(true);

        let message = '';

        message = await validatePassword();

        if (message.length > 0) {
            showMessageError(message);
            setIsSaved(false);
        }
        else {
            const oldPasswordEncrypted = encryptPassword(oldPassword);
            const passwordEncrypted = encryptPassword(password);
            const passwordAzurEncrypted = encryption(password);

            let userCode = UserCode() ?? props.userCode;

            let intervalTimeout = 0;

            // Manage prefix DB
            let connectionName = null;
            const listPrefixConnection = storage.getListPrefixConnection();

            if (listPrefixConnection != null && listPrefixConnection.length > 0) {
                connectionName = getConnectionStringNamePrefix(listPrefixConnection, userCode);
            }

            updatePassword(userCode, oldPasswordEncrypted, passwordEncrypted, passwordAzurEncrypted, connectionName).then(result => {
                if (result.status === StatusCode.SUCCESSFULL) {
                    setIsSaved(false);

                    if (result.passwordStatus !== PasswordStatusCode.PASSWORD_COMPROMISED_DENIED) {
                        showMessageSuccess(i18next.t('password:success.message.password-saved'));
                    }

                    if (result.passwordStatus === PasswordStatusCode.PASSWORD_COMPROMISED) {
                        intervalTimeout = 7000;
                        setTimeout(() => {
                            showMessageWarning(i18next.t('password:validation.message.password-compromised'), 8000);
                        }, 2500);
                    } else if (result.passwordStatus === PasswordStatusCode.PASSWORD_COMPROMISED_DENIED) {
                        showMessageError(i18next.t('password:validation.message.password-compromised-denied'));
                        return;
                    }

                    resetForm();

                    if (props.userCode && props.userCode.length > 0) {
                        //Is a popup
                        setTimeout(() => {
                            handleClose(true);
                        }, intervalTimeout);
                    } else if (props.handleClose != undefined) {
                        setTimeout(() => {
                            props.handleClose();
                        }, intervalTimeout);
                    }
                    return;
                }
                else if (result.status === StatusCode.NOT_VALIDATED) {
                    if (result.passwordStatus === PasswordStatusCode.NOT_VALID) {
                        message = i18next.t('password:validation.message.information-invalid')
                    } else if (result.passwordStatus === PasswordStatusCode.EXCEEDED_MAX_ATTEMPS) {
                        message = i18next.t('password:validation.message.exceeded-attempts')
                    } else if (result.passwordStatus === PasswordStatusCode.USER_LOCKED) {
                        message = i18next.t('password:validation.message.user-locked')
                    } else if (result.passwordStatus === PasswordStatusCode.CODE_EXPIRED) {
                        message = i18next.t('password:validation.message.expired-code')
                    } else if (result.passwordStatus === PasswordStatusCode.REUSED_PASSWORD) {
                        message = i18next.t('password:validation.message.reused-password')
                    } else if (result.passwordStatus === PasswordStatusCode.REUSE_CURRENT_PASSWORD) {
                        message = i18next.t('password:validation.message.reuse-current-password')
                    } else if (result.passwordStatus === PasswordStatusCode.OLD_PASSWORD_DOES_NOT_MATCH) {
                        message = i18next.t('password:validation.message.old_password_does_not_match')
                    }
                    else {
                        // Status code not exists
                        message = i18next.t('password:validation.message.generic-error')
                    }
                }
                else {
                    // Error
                    message = i18next.t('password:validation.message.generic-error')
                }
                showMessageError(message);
                setIsSaved(false);
            }).catch(error => {
                showMessageError(genericError);
                setIsSaved(false);
            });
        }
    };

    const resetForm = () => {
        setOldPassword('');
        setPassword('');
        setConfirmPassword('');
    }

    const onChangeOldPassword = (event) => {
        setOldPassword(event.target.value.trim());
    }

    const onChangePassword = (event) => {
        setPassword(event.target.value.trim());
    }

    const onChangeConfirmPassword = (event) => {
        setConfirmPassword(event.target.value.trim());
    }

    const showMessageSuccess = (message) => {
        alertRef.current.showMessageSuccess(message);
    }

    const showMessageError = (message) => {
        alertRef.current.showMessageError(message);
    }

    const showMessageWarning = (message, duration) => {
        alertRef.current.showMessageWarning(message, duration);
    }

    const handleMessagePassword = () => {
        const isPasswordComplex = storage.getIsPasswordComplex();
        const minLengthPassword = storage.getMinLengthPassword();

        let pwdMsg = "";

        const msgMinLength = format(i18next.t("password:description.min-length-password"), minLengthPassword);

        if (isPasswordComplex) {
            const msgSpecialChar = i18next.t("password:description.number-special-char-password");
            const msgMinTwoLetters = i18next.t("password:description.min-two-letters-password");
            const msgMinMajLetters = i18next.t("password:description.min-maj-letters-password");
            pwdMsg = msgMinLength + '\n' + msgSpecialChar + '\n' + msgMinTwoLetters + '\n' + msgMinMajLetters;
        } else {
            pwdMsg = msgMinLength;
        }

        setDescriptionPassword(pwdMsg);
    }

    function replaceWithBr() {
        return descriptionPassword.replace(/\n/g, "<br />")
    }

    const handleClickShowOldPassword = () => {
        setShowOldPassword(!showOldPassword);
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleClickShowPasswordConfirm = () => {
        setShowPasswordConfirm(!showPasswordConfirm);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };


    return (
        <div id="divPassword" className={classes.root}>
            <div className={classes.descriptionPassword} dangerouslySetInnerHTML={{ __html: replaceWithBr() }}></div>
            <FormControl fullWidth className={classes.formControl}>
                <TextField
                    id="oldPassword"
                    label={i18next.t('password:form.oldPassword')}
                    type={showOldPassword ? 'text' : 'password'}
                    value={oldPassword}
                    onChange={onChangeOldPassword}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowOldPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    className={stylesL.eyeIcon}
                                >
                                    <FontAwesomeIcon icon={showOldPassword ? faEye : faEyeSlash} className={stylesL.icon_passwordVisibility} />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </FormControl>
            <FormControl fullWidth className={classes.formControl}>
                <TextField
                    id="password"
                    label={i18next.t('password:form.newPassword')}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={onChangePassword}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    className={stylesL.eyeIcon}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} className={stylesL.icon_passwordVisibility} />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </FormControl>
            <FormControl fullWidth className={classes.formControl}>
                <TextField
                    id="confirmPassword"
                    label={i18next.t('password:form.confirmationPassword')}
                    type={showPasswordConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={onChangeConfirmPassword}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPasswordConfirm}
                                    onMouseDown={handleMouseDownPassword}
                                    className={stylesL.eyeIcon}
                                >
                                    <FontAwesomeIcon icon={showPasswordConfirm ? faEye : faEyeSlash} className={stylesL.icon_passwordVisibility} />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </FormControl>

            <div className={classes.saveButtonLine}>
                <PasswordTooltip>
                    <IconButton className={classes.lightBulbIcon}>
                        <EmojiObjectsIcon fontSize="large" />
                    </IconButton>
                </PasswordTooltip>

                <Button onClick={handleSave} loading={isSaved} variant="success" className={classes.nextButton}>
                    {i18next.t('password:button.save')}
                </Button>
            </div>
            
            <AlertAlgo ref={alertRef} />
        </div>
    );
}

export default withNamespaces(['password', 'error'], { wait: true })(ChangePassword);